/**
 * PropertyPassportReport — the 6-section auto-populated "Property Passport"
 * surfaced directly under the photos on every listing.
 *
 * UX rules:
 *   - Collapsed by default. Each card always shows a one-line teaser so the
 *     listing never looks empty.
 *   - Click a card to expand; only that card's content loads.
 *   - Sections not yet wired show a "Coming soon" pill but the card still
 *     appears (so users see the roadmap visually).
 *   - Same JSON payload powers the on-page render, the Pre-Purchase Pack PDF,
 *     and the AI assistant retrieval.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, BadgeCheck, Loader2 } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import EircodePrompt from './EircodePrompt';
import ListingAskPanel from './ListingAskPanel';
import HomeEnergySection from './passport/HomeEnergySection';
import SiteGroundSection from './passport/SiteGroundSection';
import RealCostSection from './passport/RealCostSection';
import DailyLifeSection from './passport/DailyLifeSection';
import PlanningFutureSection from './passport/PlanningFutureSection';
import FutureNeighboursSection from './passport/FutureNeighboursSection';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTION_BODY = {
  home_energy:        HomeEnergySection,
  site_ground:        SiteGroundSection,
  real_cost:          RealCostSection,
  daily_life:         DailyLifeSection,
  planning_future:    PlanningFutureSection,
  future_neighbours:  FutureNeighboursSection,
};

const PropertyPassportReport = ({ propertyId, country, property }) => {
  // Locale switch — drives Eircode vs Postcode wording, BER vs EPC etc.
  // Falls back to IE when the parent didn't pass `country` (most legacy
  // callers, before the UK launch).
  const L = (country && /^(UK|GB|United Kingdom|Great Britain|England|Scotland|Wales|Northern Ireland)$/i.test(country))
    ? { postcodeLabel: 'Postcode' }
    : { postcodeLabel: 'Eircode' };
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/api/listings/${propertyId}/passport-report`);
        if (!cancelled) setReport(r.data);
      } catch {
        if (!cancelled) setReport(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [propertyId, version]);

  if (loading) {
    return (
      <Card data-testid="passport-report-loading">
        <CardContent className="p-6 flex items-center gap-3 text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Building the Property Passport…
        </CardContent>
      </Card>
    );
  }
  if (!report || !report.sections?.length) return null;

  return (
    <Card data-testid="passport-report" className="overflow-hidden">
      <CardContent className="p-0">
        {/* Header strip */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-purple-50 to-stone-50 border-b border-stone-200">
          <div>
            <h2 className="text-xl font-bold text-stone-900 flex items-center gap-2">
              Property Passport
              <Badge className="bg-purple-100 text-purple-800 border-purple-200 text-[10px] py-0">
                AUTO
              </Badge>
            </h2>
            <p className="text-xs text-stone-500 mt-0.5">
              Independent data pulled for this {L.postcodeLabel} — open any section to dive in.
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-purple-700" data-testid="passport-completeness">
              {report.completeness}%
            </div>
            <div className="text-[10px] text-stone-500">
              {report.available}/{report.total} sections live
            </div>
          </div>
        </div>

        {/* Section cards */}
        <ul className="divide-y divide-stone-100">
          {report.sections.map((s) => (
            <SectionRow
              key={s.id}
              section={s}
              propertyId={propertyId}
              postcodeLabel={L.postcodeLabel}
              open={openId === s.id}
              onToggle={() => setOpenId(openId === s.id ? null : s.id)}
            />
          ))}
        </ul>

        {/* Inline Eircode prompt — shown to ANY viewer when missing. The
            agent will see this on their own listing; a buyer who fills it
            also helps because the next visitor gets precise data. */}
        {report.sections.some((s) => s.needs_eircode || s.is_county_level) && (
          <div className="px-6 py-4 border-t border-stone-200">
            <EircodePrompt propertyId={propertyId} onSaved={() => setVersion((v) => v + 1)} />
          </div>
        )}

        {/* Ask-the-home AI panel — grounded against the Passport JSON above */}
        <ListingAskPanel propertyId={propertyId} property={property} />

        {/* Pre-Purchase Pack PDF — opens print-styled HTML in new tab */}
        <div className="px-6 py-4 bg-stone-50 border-t border-stone-200 flex items-center justify-between">
          <div className="text-xs text-stone-500">
            Download the full <strong>Pre-Purchase Pack</strong> — every section + the agent&apos;s contact, ready to print.
          </div>
          <Button
            size="sm"
            onClick={() => window.open(`${API}/api/listings/${propertyId}/pre-purchase-pack`, '_blank', 'noopener')}
            data-testid="passport-pdf-download"
            className="text-xs bg-purple-700 hover:bg-purple-800"
          >
            Download PDF
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const SectionRow = ({ section, propertyId, postcodeLabel = 'Eircode', open, onToggle }) => {
  const Body = SECTION_BODY[section.id];
  return (
    <li data-testid={`passport-section-${section.id}`}>
      <button
        type="button"
        onClick={onToggle}
        disabled={!section.available}
        className={`w-full flex items-center gap-3 px-6 py-4 text-left transition ${
          section.available ? 'hover:bg-purple-50/40 cursor-pointer' : 'opacity-60 cursor-not-allowed'
        }`}
        data-testid={`passport-section-${section.id}-toggle`}
      >
        <span className="text-2xl" aria-hidden="true">{section.emoji}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-stone-900">{section.label}</span>
            {section.verified && (
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px] py-0">
                <BadgeCheck className="h-3 w-3 mr-0.5" /> Verified
              </Badge>
            )}
            {section.needs_eircode && (
              <Badge variant="outline" className="text-[10px] py-0 text-amber-700 border-amber-300 bg-amber-50">
                Needs {postcodeLabel}
              </Badge>
            )}
            {!section.available && !section.needs_eircode && (
              <Badge variant="outline" className="text-[10px] py-0 text-stone-500">
                Coming soon
              </Badge>
            )}
          </div>
          <div className="text-sm text-stone-600 truncate" data-testid={`passport-section-${section.id}-teaser`}>
            {section.teaser}
          </div>
        </div>
        {section.available && (
          open
            ? <ChevronUp className="h-4 w-4 text-stone-400 flex-shrink-0" />
            : <ChevronDown className="h-4 w-4 text-stone-400 flex-shrink-0" />
        )}
      </button>
      {open && section.available && Body && (
        <div className="px-6 pb-5 -mt-1" data-testid={`passport-section-${section.id}-body`}>
          <Body section={section} propertyId={propertyId} />
        </div>
      )}
    </li>
  );
};

export default PropertyPassportReport;
